import * as Sentry from "@sentry/react";

import { call, put, retry, takeEvery, takeLatest } from "redux-saga/effects";

import { AxiosResponse } from "axios";
import { BASE_API_URL } from "../../config";
import { SignOrderResponse } from "./types/SignOrderResponse";
import { actions } from "./slice";
import { createRequest } from "../../utils/requestUtils";

function* fetchSigningOrder({
  payload,
}: ReturnType<typeof actions.requestSigningOrder>) {
  const { caseId } = payload;
  const url = `${BASE_API_URL}/cases/${caseId}/signing`;
  try {
    const { data }: AxiosResponse<SignOrderResponse> = yield call(
      createRequest,
      { url: url },
    );

    yield put(actions.requestSigningOrderSuccessful(data));
  } catch (error) {
    /*
     * Sentry logging to easily identify problematic questions etc.
     * This should be made more generic
     */
    Sentry.setTag("apiUrl", url);
    Sentry.setTag("function", "fetchSigningOrder");
    Sentry.setContext("saga", {
      name: "Signing",
      function: "fetchSigningOrder",
      href: url,
    });
    Sentry.captureEvent({
      message: "Unable to fetch siging order",
    });
    const errorMessage = error instanceof Error ? error.toString() : "error";
    yield put(actions.requestSigningOrderFailed(errorMessage));
  }
}

function* createSigningOrder({
  payload,
}: ReturnType<typeof actions.requestCreateSigningOrder>) {
  const { caseId } = payload;
  const url = `${BASE_API_URL}/cases/${caseId}/signing?withEdhRef=true`;
  try {
    const { data }: AxiosResponse<SignOrderResponse> = yield call(
      createRequest,
      {
        method: "post",
        url: url,
      },
    );
    yield put(actions.requestSigningOrderSuccessful(data));
  } catch (error) {
    /*
     * Sentry logging to easily identify problematic questions etc.
     * This should be made more generic
     */
    Sentry.setTag("apiUrl", url);
    Sentry.setTag("function", "createSigningOrder");
    Sentry.setContext("saga", {
      name: "Signing",
      function: "createSigningOrder",
      href: url,
    });
    Sentry.captureEvent({
      message: "Unable to create signing order",
    });

    const errorMessage = error instanceof Error ? error.toString() : "error";
    yield put(actions.requestSigningOrderFailed(errorMessage));
  }
}

function* completeSigning({
  payload,
}: ReturnType<typeof actions.requestSigningCompleted>) {
  const { caseId, signingReference, edhSigningReference } = payload;

  const url = edhSigningReference
    ? `${BASE_API_URL}/cases/${caseId}/signing/edhReference/${edhSigningReference}`
    : `${BASE_API_URL}/cases/${caseId}/signing/${signingReference}`;

  try {
    const { data }: AxiosResponse = yield retry(3, 1500, createRequest, {
      method: "patch",
      url: url,
      requestBody: { caseId: caseId, signingReference: signingReference },
    });

    yield put(actions.requestSigningCompletedSuccessful(data));
  } catch (error) {
    /*
     * Sentry logging to easily identify problematic questions etc.
     * This should be made more generic
     */
    Sentry.setTag("apiUrl", url);
    Sentry.setTag("function", "completeSigning");
    Sentry.setContext("saga", {
      name: "Signing",
      function: "completeSigning",
      href: url,
    });
    Sentry.captureEvent({
      message: "Unable to complete signing",
    });

    const errorMessage = error instanceof Error ? error.toString() : "error";
    yield put(actions.requestSigningCompletedFailed(errorMessage));
  }
}

function* abortSigning({
  payload,
}: ReturnType<typeof actions.requestSigningAborted>) {
  const { caseId, signingReference, edhSigningReference } = payload;

  const url = edhSigningReference
    ? `${BASE_API_URL}/cases/${caseId}/signing/edhReference/${edhSigningReference}`
    : `${BASE_API_URL}/cases/${caseId}/signing/${signingReference}`;

  try {
    const { data }: AxiosResponse = yield retry(3, 1500, createRequest, {
      method: "delete",
      url: url,
      requestBody: {
        data: { caseId: caseId, signingReference: signingReference },
      },
    });

    yield put(actions.requestSigningAbortedSuccessful(data));
  } catch (error) {
    /*
     * Sentry logging to easily identify problematic questions etc.
     * This should be made more generic
     */
    Sentry.setTag("apiUrl", url);
    Sentry.setTag("function", "abortSigning");
    Sentry.setContext("saga", {
      name: "Signing",
      function: "abortSigning",
      href: url,
    });
    Sentry.captureEvent({
      message: "Unable to abort signing",
    });
    const errorMessage = error instanceof Error ? error.toString() : "error";
    yield put(actions.requestSigningAbortedFailed(errorMessage));
  }
}

function* signingSaga() {
  yield takeLatest(actions.requestSigningOrder, fetchSigningOrder);
  yield takeEvery(actions.requestCreateSigningOrder, createSigningOrder);
  yield takeLatest(actions.requestSigningCompleted, completeSigning);
  yield takeEvery(actions.requestSigningAborted, abortSigning);
}

export default signingSaga;
